import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

/** API */
import ImportInventoryApi from 'api/warehouses/import-inventory.api';
import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/** STYLE */

/** UTILS */
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { showModal } from 'app/slice/modal.slice';
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils';

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, InputAdornment } from '@mui/material';
import HeaderCard from 'component/CardCustom/HeaderCard';
import { notiError } from 'component/notifications/notifications';
import TableCustom from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import _ from 'lodash';
import moment from 'moment';
import MainButton from 'new-components/buttons/main-button';
import DateRangeInput from 'new-components/inputs/date-range-input';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import { dateToView } from '../../../utils/date.utils';
import PopupCancelImport from './popupCancelImport';

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}


export default function ImportProduct() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const [documents, setDocuments] = useState([])
  const [document, setDocument]: any = useState({})
  const [popupCancel, setPopupCancel] = useState(false);

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [rangeDate, setRangeDate] = useState(['', '']);

  const loadData = async () => {
    try {
      const params: any = {
        page,
        pageLimit,
        sortBy,
        sortType,
        status: status === 'ALL' ? '' : status,
      }
      if (search) params.search = search
      if (rangeDate) {
        params.dateStart = rangeDate[0]
        params.dateEnd = rangeDate[1]
      }
      const resp = await ImportInventoryApi.findAllDocument(params)
      if (resp.status === 200) {
        setRowCount(Number(resp.headers['x-total'] || 0))
        setDocuments(resp.data)
      } else {
        setDocuments([])
      }
    } catch (err) {
      notiError(t('ERROR'))
      setDocuments([])
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])


  const onCreate = () => {
    window.location.href = routeName.importProduct + '/create'
  }

  const onList = (data: any) => {
    history.push(routeName.importProduct + '/list', data)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const onCancel = (detail: any) => {
    setDocument(detail)
    dispatch(showModal())
    setPopupCancel(true)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO') },
    { id: 'date', disablePadding: false, label: t('IMPORT_PRODUCT.TABLE.DATE') },
    { id: 'documentNo', disablePadding: false, label: t('IMPORT_PRODUCT.TABLE.DOCUMENT_NO') },
    { id: 'subject', disablePadding: false, label: t('IMPORT_PRODUCT.TABLE.DOCUMENT_NAME') },
    { id: 'status', disablePadding: false, label: t('IMPORT_PRODUCT.TABLE.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('IMPORT_PRODUCT.TABLE.UPDATEDBY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { inventoryDocumentId, documentNo, prefix, date, subject, updatedBy, updatedAt, status: importProductStatus, cInven } = objData

    const statusBtnActive = importProductStatus === 'ACTIVE' ? (cInven > 0 ? true : permissions.isUpdate.disabled) : true
    const objRenderData = {
      key: inventoryDocumentId,
      id: inventoryDocumentId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no, class: 'cursor-pointer' },
        { option: 'TEXT', align: 'left', label: dateToView(date), class: 'cursor-pointer' },
        { option: 'TEXT', align: 'left', label: ((prefix && documentNo) && `${prefix}-${documentNo}`) || documentNo || '-', class: 'cursor-pointer' },
        { option: 'TEXT', align: 'left', label: subject, class: 'cursor-pointer' },
        { option: 'TEXT', align: 'center', label: t(`STATUS_NO_ICON.${importProductStatus}`), class: 'cursor-pointer' },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt }, class: 'cursor-pointer' },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          newIconAction: true,
          values: [
            { option: 'STATUS_INACTIVE', label: t(`IMPORT_PRODUCT.TABLE.CANCEL_DOCUMENT`), disabled: statusBtnActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'VIEW', label: t(`BUTTON.VIEW`), },
          ]
        }
      ]
    }
    return <TableRowCommon
      {...objRenderData}
      oninactive={() => onCancel(objRenderData.obj)}
      onView={() => onList(objRenderData.obj)}
    />
  }

  return (
    <Box>
      <HeaderCard text={t('IMPORT_PRODUCT.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('IMPORT_PRODUCT.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS_NO_ICON.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS_NO_ICON.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <DateRangeInput
              allowClear
              value={rangeDate || ['', '']}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(['', ''])
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              onClear={() => {
                setPage(1)
                setRangeDate(['', ''])
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={12} xl={6}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />
              <MainButton
                title={t('IMPORT_PRODUCT.BUTTON.ADD_IMPORT_PRODUCT')}
                type='primary'
                variant='contained'
                startIcon={<AddCircleIcon />}
                onClick={onCreate} />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={documents.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
      </Box>
      {popupCancel && (
        <PopupCancelImport
          inventoryDocumentId={document.inventoryDocumentId}
          document={{ prefix: document.prefix, documentNo: document.documentNo }}
          onCancel={(show: boolean) => {
            setPopupCancel(show)
            setDocument({})
          }}
          onAfterSubmit={(show: boolean) => {
            setPopupCancel(show)
            setDocument({})
            loadData()
          }}
        />
      )}
    </Box>
  )
}
